// Áudio: dois cards lado a lado (áudio → texto e texto → áudio) e, abaixo, as vozes do cliente (listar, treinar, apagar).
// Tudo passa por /api/client/audio/*: o client-api fala com os serviços de voz (XTTS, treino, transcrição); aqui só há UI.
// Aviso de privacidade: a voz clonada é dado pessoal sensível; o envio exige a confirmação de autorização do dono da voz.
import { useCallback, useEffect, useRef, useState, type DragEvent, type KeyboardEvent, type MouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, Download, FileAudio, Mic, Plus, Square, Trash2, TriangleAlert, Upload, X } from 'lucide-react';
import { api, apiBinary, ApiError, brl, when, type AudioStatus, type AudioUsage, type AudioVoice, type SttJob, type VoiceMode } from '../api';
import { toast } from '../components/toast';
import { toBase64, two, useRecorder } from '../recorder';
import { CopyButton, copyText } from '../components/CopyButton';
import { NAME_MARK, SCRIPT_PRO, SCRIPT_QUICK, VOICE_TIPS, scriptText, type VoiceScript } from '../voiceScripts';
import '../styles/audio.css';

const MAX_BYTES = 120 * 1024 * 1024;   // mesmo teto do servidor (áudio decodificado)
const MAX_CHARS = 2000;
const SEC_PER_CHAR = 0.08;             // síntese em CPU: ~80 ms por caractere (estimativa mostrada ao usuário)
const MAX_VOICES = 10;
const AUDIO_EXT = /\.(mp3|wav|m4a|aac|ogg|oga|opus|flac|webm|mp4|m4v|mov|mkv|wma|amr|3gp|aiff?)$/i;
const ACCEPT = 'audio/*,video/mp4,video/webm,video/quicktime,.m4a,.mp3,.wav,.ogg,.opus,.flac,.aac,.webm,.mp4,.mov,.mkv';
const RANGE: Record<VoiceMode, { min: number; max: number; text: string }> = {
  instant: { min: 10, max: 180, text: 'A voz Rápida pede um áudio de 10 segundos a 3 minutos' },
  professional: { min: 120, max: 600, text: 'O treino Profissional pede de 2 a 10 minutos de áudio limpo' },
};
const STATE_LABEL: Record<AudioVoice['status'], string> = { ready: 'pronta', queued: 'na fila', processing_audio: 'processando áudio', training: 'treinando', failed: 'falhou' };

const mmss = (s: number) => `${two(s / 60)}:${two(s % 60)}`;
const fmtDur = (s: number) => (s < 60 ? `${Math.round(s)} s` : `${Math.floor(s / 60)} min ${two(s % 60)} s`);
const fmtSize = (b: number) => {
  const n = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 1 });
  return b < 1024 ? `${b} B` : b < 1048576 ? `${n(b / 1024)} KB` : `${n(b / 1048576)} MB`;
};

/** Duração pelos metadados do navegador; se eles não trazem (webm, alguns vídeos), decodifica o áudio (só até 80 MB). Nulo = não deu para saber. */
async function readDuration(file: Blob): Promise<number | null> {
  const meta = await new Promise<number | null>((ok) => {
    const url = URL.createObjectURL(file);
    const a = document.createElement('audio');
    a.preload = 'metadata';
    const done = (v: number | null) => { clearTimeout(t); a.removeAttribute('src'); a.load(); URL.revokeObjectURL(url); ok(v); };
    const t = setTimeout(() => done(null), 8000);
    a.onloadedmetadata = () => done(Number.isFinite(a.duration) && a.duration > 0 ? a.duration : null);
    a.onerror = () => done(null);
    a.src = url;
  });
  if (meta !== null || file.size > 80 * 1024 * 1024) return meta;
  try {
    const ctx = new AudioContext();
    const buf = await ctx.decodeAudioData(await file.arrayBuffer());
    void ctx.close();
    return buf.duration;
  } catch { return null; }
}

type Picked = { file: File; duration: number | null; recorded: boolean };

/** Escolha do áudio: anexar (botão ou arrastar) ou gravar. O mesmo componente serve ao card de transcrição e ao treino de voz. */
function AudioSource({ id, picked, onPick, disabled = false, recordMin }: { id: string; picked: Picked | null; onPick: (p: Picked | null) => void; disabled?: boolean; recordMin?: number }) {
  const [reading, setReading] = useState(false);
  const [drag, setDrag] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const rec = useRecorder((file, secs) => { setErr(null); onPick({ file, duration: secs, recorded: true }); });

  const pick = async (f: File | undefined) => {
    if (!f) return;
    setErr(null);
    if (!(f.type.startsWith('audio/') || f.type.startsWith('video/') || AUDIO_EXT.test(f.name))) { setErr('esse arquivo não parece ser um áudio'); return; }
    if (f.size > MAX_BYTES) { setErr('arquivo grande demais (máximo 120 MB)'); return; }
    if (f.size < 1024) { setErr('arquivo vazio ou curto demais'); return; }
    setReading(true);
    const duration = await readDuration(f);
    setReading(false);
    onPick({ file: f, duration, recorded: false });
  };
  const onDrop = (e: DragEvent) => { e.preventDefault(); setDrag(false); if (!disabled && !rec.recording) void pick(e.dataTransfer.files[0]); };
  const busy = disabled || reading || rec.recording;

  return (
    <div className={`aud-src${drag ? ' drag' : ''}`}
      onDragOver={(e) => { if (Array.from(e.dataTransfer.types).includes('Files')) { e.preventDefault(); setDrag(true); } }}
      onDragLeave={() => setDrag(false)} onDrop={onDrop}>
      <input ref={fileRef} id={`${id}-file`} type="file" hidden accept={ACCEPT} aria-label="Anexar arquivo de áudio" onChange={(e) => { void pick(e.target.files?.[0]); e.target.value = ''; }} />
      {rec.recording ? (
        <div className="aud-rec" role="group" aria-label="Gravando">
          <span className="aud-rec-dot" aria-hidden="true" />
          {recordMin == null ? (
            <>
              <b className="mono" aria-hidden="true">{mmss(rec.seconds)}</b>
              <span className="aud-rec-txt">gravando…</span>
            </>
          ) : (
            <span className={`aud-rec-txt mono${rec.seconds >= recordMin ? ' ok' : ''}`}>
              <b>{mmss(rec.seconds)}</b> gravando · mínimo {mmss(recordMin)}{rec.seconds >= recordMin ? ' · já passou do mínimo' : ` · atual ${mmss(rec.seconds)}`}
            </span>
          )}
          <button type="button" className="btn" onClick={rec.stop}><Square />Parar</button>
        </div>
      ) : picked ? (
        <div className="aud-file">
          <span className="aud-file-ico"><FileAudio /></span>
          <span className="aud-file-main">
            <b title={picked.file.name}>{picked.file.name}</b>
            <span className="mono">{picked.duration != null ? fmtDur(picked.duration) : 'duração desconhecida'} · {fmtSize(picked.file.size)}{picked.recorded ? ' · gravado agora' : ''}</span>
          </span>
          <button type="button" className="aud-x" onClick={() => { setErr(null); onPick(null); }} disabled={disabled} aria-label="Remover o áudio escolhido"><X /></button>
        </div>
      ) : (
        <div className="aud-drop">
          <Upload />
          <span>{reading ? 'lendo o arquivo…' : 'Arraste um áudio aqui, anexe um arquivo ou grave pelo navegador'}</span>
        </div>
      )}
      {!rec.recording && (
        <div className="aud-src-btns">
          <button type="button" className="btn" onClick={() => fileRef.current?.click()} disabled={busy}><Upload />{picked ? 'Trocar arquivo' : 'Anexar arquivo'}</button>
          <button type="button" className="btn" onClick={() => void rec.start()} disabled={busy || !rec.supported} title={rec.supported ? undefined : 'Este navegador não permite gravar'}><Mic />Gravar</button>
        </div>
      )}
      <span className="sr-only" role="status" aria-live="polite">{rec.recording ? 'Gravando' : reading ? 'Lendo o arquivo' : ''}</span>
      {(err || rec.error) && <p className="aud-err" role="alert">{err ?? rec.error}</p>}
    </div>
  );
}

// ---------------------------------------------------------------- Áudio → texto

function SttCard({ available, onUsed }: { available: boolean | null; onUsed: () => void }) {
  const [picked, setPicked] = useState<Picked | null>(null);
  const [lang, setLang] = useState<'pt' | 'auto'>('pt');
  const [phase, setPhase] = useState<'idle' | 'sending' | 'running' | 'done' | 'error'>('idle');
  const [progress, setProgress] = useState<number | null>(null);
  const [queued, setQueued] = useState(true);
  const [result, setResult] = useState<SttJob | null>(null);
  const [withTimes, setWithTimes] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => { alive.current = true; return () => { alive.current = false; if (timer.current) clearTimeout(timer.current); }; }, []);

  // consulta o job a cada 2 s (um pedido por vez); falha de rede passageira não derruba, 404 (job expirado) sim
  const poll = useCallback(async (id: string, fails: number) => {
    try {
      const j = await api<SttJob>(`/api/client/audio/stt/${id}`);
      if (!alive.current) return;
      if (j.status === 'done') { setResult(j); setPhase('done'); onUsed(); return; }
      if (j.status === 'failed') { setError(j.error ?? 'a transcrição falhou'); setPhase('error'); onUsed(); return; }
      setProgress(j.progress); setQueued(j.status === 'queued');
      timer.current = setTimeout(() => void poll(id, 0), 2000);
    } catch (e) {
      if (!alive.current) return;
      if (e instanceof ApiError && (e.status === 404 || e.status === 401)) { setError(e.status === 404 ? 'a transcrição expirou; envie o áudio de novo' : 'sua sessão expirou'); setPhase('error'); return; }
      if (fails >= 4) { setError((e as Error).message); setPhase('error'); return; }
      timer.current = setTimeout(() => void poll(id, fails + 1), 2000);
    }
  }, [onUsed]);

  const start = async () => {
    if (!picked) return;
    setError(null); setResult(null); setProgress(null); setQueued(true); setPhase('sending');
    try {
      const audioBase64 = await toBase64(picked.file);
      const { id } = await api<{ id: string }>('/api/client/audio/stt', { method: 'POST', body: JSON.stringify({ audioBase64, filename: picked.file.name, language: lang }) });
      if (!alive.current) return;
      setPhase('running');
      void poll(id, 0);
    } catch (e) { if (alive.current) { setError((e as Error).message); setPhase('error'); } }
  };
  const reset = () => { if (timer.current) clearTimeout(timer.current); setPhase('idle'); setResult(null); setError(null); setPicked(null); setProgress(null); };

  const text = result
    ? (withTimes && result.segments?.length ? result.segments.map((s) => `[${mmss(s.start)}] ${s.text.trim()}`).join('\n') : result.text ?? '')
    : '';
  const download = () => {
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `${(picked?.file.name ?? 'transcricao').replace(/\.[^.]+$/, '') || 'transcricao'}.txt`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const working = phase === 'sending' || phase === 'running';
  const pct = progress != null ? Math.round(Math.min(1, Math.max(0, progress)) * 100) : null;

  return (
    <section className="aud-card" aria-labelledby="aud-stt-h">
      <div className="aud-card-h"><span className="eyebrow">Entrada</span><h2 id="aud-stt-h">Áudio → texto</h2></div>
      <p className="aud-lead">Anexe uma gravação (até 120 MB, cerca de 60 minutos) ou grave aqui e receba a transcrição.</p>
      {available === false && <p className="aud-note" role="status">Nenhum nó de áudio disponível para transcrever agora.</p>}

      {phase !== 'done' && (
        <>
          <AudioSource id="aud-stt" picked={picked} onPick={setPicked} disabled={working} />
          <div className="aud-row">
            <label className="field aud-lang"><span>Idioma</span>
              <select value={lang} onChange={(e) => setLang(e.target.value as 'pt' | 'auto')} disabled={working}>
                <option value="pt">Português</option><option value="auto">Detectar automático</option>
              </select>
            </label>
            <button type="button" className="btn primary" onClick={() => void start()} disabled={!picked || working || available === false}>
              {phase === 'sending' ? 'Enviando…' : phase === 'running' ? 'Transcrevendo…' : 'Transcrever'}
            </button>
          </div>
        </>
      )}

      {working && (
        <div className="aud-prog" aria-live="polite">
          <div className="aud-bar" role="progressbar" aria-label="Progresso da transcrição" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct ?? undefined}>
            <i className={pct == null ? 'indet' : ''} style={pct == null ? undefined : { width: `${pct}%` }} />
          </div>
          <span className="mono">{phase === 'sending' ? 'enviando o áudio…' : queued ? 'na fila…' : pct != null ? `transcrevendo… ${pct}%` : 'transcrevendo…'}</span>
        </div>
      )}
      {phase === 'error' && error && <p className="aud-err" role="alert">{error}</p>}

      {phase === 'done' && result && (
        <div className="aud-result">
          <div className="aud-result-h">
            <span className="mono">{result.language ? `idioma: ${result.language}` : 'transcrição'}{result.duration != null ? ` · ${fmtDur(result.duration)}` : ''}</span>
            {result.segments && result.segments.length > 0 && (
              <label className="aud-check"><input type="checkbox" checked={withTimes} onChange={(e) => setWithTimes(e.target.checked)} /><span>com tempos</span></label>
            )}
          </div>
          <textarea readOnly rows={10} value={text} aria-label="Texto transcrito" placeholder="(nenhuma fala encontrada)" />
          <div className="aud-row aud-row-end">
            <CopyButton text={text} label="Copiar a transcrição" />
            <button type="button" className="btn" onClick={download} disabled={!text}><Download />Baixar .txt</button>
            <button type="button" className="btn" onClick={reset}>Nova transcrição</button>
          </div>
        </div>
      )}
      {phase === 'idle' && !picked && <p className="aud-hint">A transcrição aparece aqui, com opção de copiar, baixar e ver os tempos.</p>}
    </section>
  );
}

// ---------------------------------------------------------------- Texto → áudio

function TtsCard({ voices, available, onUsed }: { voices: AudioVoice[] | null; available: boolean | null; onUsed: () => void }) {
  const [text, setText] = useState('');
  const [voice, setVoice] = useState('');
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ctl = useRef<AbortController | null>(null);
  const urlRef = useRef<string | null>(null);
  const len = text.length;
  const est = Math.max(1, Math.ceil(len * SEC_PER_CHAR));

  const setAudioUrl = (u: string | null) => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); urlRef.current = u; setUrl(u); };
  useEffect(() => () => { ctl.current?.abort(); if (urlRef.current) URL.revokeObjectURL(urlRef.current); }, []);   // saiu da página: cancela a síntese e libera o áudio
  useEffect(() => {
    if (!busy) return;
    const t0 = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(t);
  }, [busy]);
  useEffect(() => { if (voice && !(voices ?? []).some((v) => v.id === voice && v.status === 'ready')) setVoice(''); }, [voices, voice]);   // a voz escolhida foi apagada

  const generate = async () => {
    const t = text.trim();
    if (!t || busy) return;
    const c = new AbortController();
    ctl.current = c; setBusy(true); setElapsed(0); setError(null);
    try {
      const blob = await apiBinary('/api/client/audio/tts', { text: t, ...(voice ? { voice } : {}) }, c.signal);
      setAudioUrl(URL.createObjectURL(blob));
      onUsed();
    } catch (e) {
      if (c.signal.aborted) return;
      setError(e instanceof ApiError && e.status === 401 ? 'sua sessão expirou; entre de novo' : (e as Error).message);
    } finally { if (ctl.current === c) { ctl.current = null; setBusy(false); } }
  };
  const cancel = () => { ctl.current?.abort(); ctl.current = null; setBusy(false); };

  return (
    <section className="aud-card" aria-labelledby="aud-tts-h">
      <div className="aud-card-h"><span className="eyebrow">Saída</span><h2 id="aud-tts-h">Texto → áudio</h2></div>
      <p className="aud-lead">Escreva o texto e escolha a voz. A geração roda em CPU e leva cerca de {Math.round(SEC_PER_CHAR * 1000)} ms por caractere.</p>
      {available === false && <p className="aud-note" role="status">Nenhum nó de áudio disponível para gerar voz agora.</p>}
      <label className="field"><span>Texto</span>
        <textarea rows={7} maxLength={MAX_CHARS} value={text} onChange={(e) => setText(e.target.value)} disabled={busy} placeholder="Digite ou cole o texto que deve virar áudio…" />
      </label>
      <div className="aud-count mono" aria-live="off"><span className={len >= MAX_CHARS ? 'full' : ''}>{len}/{MAX_CHARS}</span>{len > 0 && <span> · estimativa ~{est} s</span>}</div>
      <div className="aud-row">
        <label className="field aud-voice"><span>Voz</span>
          <select value={voice} onChange={(e) => setVoice(e.target.value)} disabled={busy}>
            <option value="">Padrão</option>
            {(voices ?? []).map((v) => (
              <option key={v.id} value={v.id} disabled={v.status !== 'ready'}>{v.label}{v.status === 'ready' ? '' : ` — ${STATE_LABEL[v.status]}`}</option>
            ))}
          </select>
        </label>
        {busy ? (
          <button type="button" className="btn" onClick={cancel}><X />Cancelar</button>
        ) : (
          <button type="button" className="btn primary" onClick={() => void generate()} disabled={!text.trim() || available === false}>Gerar áudio</button>
        )}
      </div>
      {busy && (
        <div className="aud-prog" role="status" aria-live="polite">
          <div className="aud-bar" role="progressbar" aria-label="Gerando o áudio" aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.min(95, Math.round((elapsed / est) * 100))}>
            <i style={{ width: `${Math.min(95, (elapsed / est) * 100)}%` }} />
          </div>
          <span className="mono">gerando… ~{est} s · {mmss(elapsed)}</span>
        </div>
      )}
      {error && <p className="aud-err" role="alert">{error}</p>}
      {url ? (
        <div className="aud-player">
          <audio controls src={url} aria-label="Áudio gerado" />
          <a className="btn" href={url} download="clusterizze-voz.wav"><Download />Baixar .wav</a>
        </div>
      ) : !busy && !error && <p className="aud-hint">O áudio gerado aparece aqui, com player e download.</p>}
    </section>
  );
}

// ---------------------------------------------------------------- Minhas vozes

/** Roteiro de leitura do modo escolhido: texto grande, `[seu nome]` destacado, copiar e dicas recolhíveis. */
function ScriptPanel({ script }: { script: VoiceScript }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const copy = async () => {
    if (!(await copyText(scriptText(script)))) { toast('não foi possível copiar', 'err'); return; }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1500);
  };
  const withName = (t: string) => t.split(NAME_MARK).flatMap((chunk, i) => (i === 0 ? [chunk] : [<mark key={i} className="aud-name">seu nome</mark>, chunk]));
  return (
    <aside className="aud-script" aria-label="Roteiro de leitura">
      <div className="aud-script-head">
        <div>
          <span className="eyebrow">Roteiro de leitura</span>
          <h3>{script.title}</h3>
        </div>
        <button type="button" className="btn sm" onClick={() => void copy()} aria-label="Copiar roteiro"><Copy />{copied ? 'Copiado' : 'Copiar roteiro'}</button>
        <span className="sr-only" role="status" aria-live="polite">{copied ? 'Roteiro copiado' : ''}</span>
      </div>
      <p className="aud-script-meta"><span className="pill fil">{script.estimate}</span><span>{script.goal}</span></p>
      <details className="aud-before">
        <summary>Antes de gravar</summary>
        <ul>{VOICE_TIPS.map((t) => <li key={t}>{t}</li>)}</ul>
      </details>
      <div className="aud-script-text" tabIndex={0} role="region" aria-label={script.title}>
        {script.parts.map((part, i) => (
          <div key={i} className="aud-part">
            {part.title && <h4><i>{i + 1}</i>{part.title}{part.hint && <em>{part.hint}</em>}</h4>}
            {part.paragraphs.map((t, j) => <p key={j}>{withName(t)}</p>)}
          </div>
        ))}
      </div>
    </aside>
  );
}

/** "Treinar nova voz": modal com o formulário à esquerda e o roteiro de leitura (do modo escolhido) à direita. */
function TrainModal({ status, onClose, onCreated }: { status: AudioStatus | null; onClose: () => void; onCreated: (v: AudioVoice) => void }) {
  const [name, setName] = useState('');
  const [mode, setMode] = useState<VoiceMode>('instant');
  const [picked, setPicked] = useState<Picked | null>(null);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const down = mode === 'professional' ? status?.train === false : status?.tts === false;
  const r = RANGE[mode];
  const durErr = !picked ? null
    : picked.duration == null ? 'não foi possível ler a duração deste arquivo; use WAV, MP3 ou M4A, ou grave pelo navegador'
    : picked.duration < r.min || picked.duration > r.max ? `${r.text} (este tem ${fmtDur(picked.duration)}).` : null;
  const can = name.trim().length > 0 && !!picked && !durErr && consent && !busy && !down;

  const submit = async () => {
    if (!can || !picked) return;
    setBusy(true); setError(null);
    try {
      const audioBase64 = await toBase64(picked.file);
      const v = await api<AudioVoice>('/api/client/audio/voices', { method: 'POST', body: JSON.stringify({ label: name.trim(), mode, audioBase64, filename: picked.file.name, consent: true }) });
      toast(mode === 'professional' ? 'Treino iniciado' : 'Voz criada');
      onCreated(v);   // o pai fecha o modal
    } catch (e) { setError((e as Error).message); setBusy(false); }
  };

  const tryClose = () => { if (!busy) onClose(); };
  const onOverlayDown = (e: MouseEvent<HTMLDivElement>) => { if (e.target !== e.currentTarget) return; e.preventDefault(); tryClose(); };
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); tryClose(); return; }
    if (e.key !== 'Tab') return;   // prende o foco dentro do modal
    const f = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('input, select, textarea, summary, button, [tabindex="0"]') ?? [])
      .filter((el) => !el.hasAttribute('disabled') && !el.hasAttribute('hidden') && el.tabIndex >= 0 && el.getClientRects().length > 0);
    if (!f.length) return;
    const first = f[0], last = f[f.length - 1];
    if (e.shiftKey && (document.activeElement === first || !panelRef.current?.contains(document.activeElement))) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  };

  return createPortal(
    <div className="aud-overlay" onMouseDown={onOverlayDown}>
      <div className="aud-modal" ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="aud-modal-h" onKeyDown={onKey}>
        <div className="aud-modal-head">
          <h2 id="aud-modal-h">Treinar nova voz</h2>
          <button type="button" className="aud-x" onClick={tryClose} disabled={busy} aria-label="Fechar"><X /></button>
        </div>
        <div className="aud-modal-body">
          <form id="aud-train-form" className="aud-form" onSubmit={(e) => { e.preventDefault(); void submit(); }}>
            <label className="field"><span>Nome da voz</span>
              <input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} placeholder="Ex.: Voz da Ana" disabled={busy} autoFocus />
            </label>
            <fieldset className="aud-modes" disabled={busy}>
              <legend>Modo</legend>
              <label className="aud-mode"><input type="radio" name="aud-mode" checked={mode === 'instant'} onChange={() => setMode('instant')} />
                <span><b>Rápida</b><em>Pronta em segundos. Amostra de 10 s a 3 min.</em></span></label>
              <label className={`aud-mode${status?.train === false ? ' off' : ''}`}><input type="radio" name="aud-mode" checked={mode === 'professional'} onChange={() => setMode('professional')} disabled={status?.train === false} />
                <span><b>Profissional</b><em>{status?.train === false ? 'Indisponível: o serviço de treino está fora do ar.' : 'Treino mais fiel, leva alguns minutos. De 2 a 10 minutos de áudio limpo.'}</em></span></label>
            </fieldset>
            <div className="aud-field"><span className="aud-lab">Áudio da voz</span>
              <AudioSource id="aud-train" picked={picked} onPick={setPicked} disabled={busy} recordMin={r.min} />
              {durErr && <p className="aud-err" role="alert">{durErr}</p>}
            </div>
            <ul className="aud-tips">
              <li>Grave em ambiente silencioso, sem eco.</li>
              <li>Uma só pessoa falando, sem música ou ruído ao fundo.</li>
              <li>Fale de forma natural, no ritmo normal, com frases completas.</li>
            </ul>
            <label className="aud-consent"><input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} disabled={busy} />
              <span>Tenho autorização da pessoa dona desta voz para cloná-la e usá-la. Entendo que a voz é um dado pessoal sensível.</span></label>
            {error && <p className="aud-err" role="alert">{error}</p>}
          </form>
          <ScriptPanel script={mode === 'professional' ? SCRIPT_PRO : SCRIPT_QUICK} />
        </div>
        <div className="aud-modal-foot">
          {down && <span className="aud-why">{mode === 'professional' ? 'serviço de treino fora do ar' : 'serviço de voz fora do ar'}</span>}
          <button type="button" className="btn" onClick={tryClose} disabled={busy}>Cancelar</button>
          <button type="submit" form="aud-train-form" className="btn primary" disabled={!can}>{busy ? (mode === 'professional' ? 'Enviando o treino…' : 'Criando a voz…') : 'Treinar voz'}</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function VoicesSection({ voices, error, status, onReload, onCreated, onGone }: {
  voices: AudioVoice[] | null; error: string | null; status: AudioStatus | null; onReload: () => void; onCreated: (v: AudioVoice) => void; onGone: (id: string) => void;
}) {
  const [confirming, setConfirming] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);
  const [training, setTraining] = useState(false);   // modal "Treinar nova voz"
  const openBtn = useRef<HTMLButtonElement>(null);
  const closeModal = () => { setTraining(false); openBtn.current?.focus(); };

  const del = async (v: AudioVoice) => {
    setConfirming(null); setDeleting(v.id);
    let gone = false;   // 404: já não existia (apagada em outra aba)
    try { await api(`/api/client/audio/voices/${v.id}`, { method: 'DELETE' }); }
    catch (e) { if (!(e instanceof ApiError && e.status === 404)) { toast((e as Error).message, 'err'); setDeleting(null); return; } gone = true; }
    setDeleting(null); onGone(v.id);
    toast(gone ? 'Essa voz já tinha sido apagada' : 'Voz apagada');
  };

  return (
    <section className="sec aud-voices" aria-labelledby="aud-voices-h">
      <div className="sec-h">
        <h2 id="aud-voices-h">Minhas vozes</h2>
        <span className="aud-sec-act">
          {voices && voices.length > 0 && <span className="n">{voices.length}/{MAX_VOICES}</span>}
          <button type="button" ref={openBtn} className="btn primary" onClick={() => setTraining(true)} aria-haspopup="dialog"><Plus />Treinar nova voz</button>
        </span>
      </div>
      <div className="aud-voices-grid">
        <div>
          {error ? (
            <div className="empty"><b>Não foi possível carregar</b><p>{error}</p><button type="button" className="btn" onClick={onReload}>Tentar de novo</button></div>
          ) : voices === null ? (
            <div className="aud-skel" aria-hidden="true"><div className="skel" /><div className="skel" /></div>
          ) : voices.length === 0 ? (
            <div className="empty"><b>Nenhuma voz treinada</b><p>A voz Padrão já está disponível no card Texto → áudio. Treine uma voz sua no botão acima para usá-la nas gerações.</p></div>
          ) : (
            <ul className="aud-vlist" aria-live="polite">
              {voices.map((v) => {
                const training = v.status === 'queued' || v.status === 'processing_audio' || v.status === 'training';
                return (
                  <li key={v.id} className="aud-v">
                    <span className="aud-v-main">
                      <b title={v.label}>{v.label}</b>
                      <span className="aud-v-meta mono">{when(v.createdAt)}</span>
                      {training && <em className="aud-v-note">treinando… pode levar alguns minutos</em>}
                      {v.status === 'failed' && v.error && <em className="aud-v-note err">{v.error}</em>}
                    </span>
                    <span className="pill">{v.mode === 'instant' ? 'Rápida' : 'Profissional'}</span>
                    <span className={`pill ${v.status === 'ready' ? 'ok' : v.status === 'failed' ? 'err' : 'fil'}`}>{STATE_LABEL[v.status]}</span>
                    <span className="aud-v-acts" onKeyDown={(e) => { if (e.key === 'Escape') setConfirming(null); }}>
                      {confirming === v.id ? (
                        <>
                          <button type="button" className="danger" onClick={() => void del(v)} aria-label={`Confirmar: apagar a voz ${v.label}`}><Check /></button>
                          <button type="button" onClick={() => setConfirming(null)} aria-label="Cancelar" autoFocus><X /></button>
                        </>
                      ) : (
                        <button type="button" className="danger" onClick={() => setConfirming(v.id)} disabled={deleting === v.id} aria-label={`Apagar a voz ${v.label}`} title="Apagar"><Trash2 /></button>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
      {training && <TrainModal status={status} onClose={closeModal} onCreated={(v) => { onCreated(v); closeModal(); }} />}
    </section>
  );
}

// ---------------------------------------------------------------- Seus últimos usos (medição da rede)

const KIND_LABEL: Record<AudioUsage['kind'], string> = { stt: 'Áudio → texto', tts: 'Texto → áudio' };
const USE_STATE: Record<string, [string, string]> = { done: ['ok', 'concluído'], error: ['err', 'falhou'], canceled: ['', 'cancelado'], pending: ['fil', 'na fila'], running: ['fil', 'em andamento'] };
const fmtUnits = (u: AudioUsage) => (u.units == null ? '—' : u.unitKind === 's' ? fmtDur(u.units) : `${Math.round(u.units).toLocaleString('pt-BR')} caracteres`);

function UsageSection({ tick }: { tick: number }) {
  const [items, setItems] = useState<AudioUsage[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    api<AudioUsage[]>('/api/client/audio/usage').then((l) => { if (live) { setItems(l); setError(null); } }).catch((e) => { if (live) setError((e as Error).message); });
    return () => { live = false; };
  }, [tick]);
  return (
    <section className="sec aud-usage" aria-labelledby="aud-usage-h">
      <div className="sec-h"><h2 id="aud-usage-h">Seus últimos usos</h2><span className="n">medido pela rede · sem guardar texto nem áudio</span></div>
      {error ? <div className="empty"><b>Não foi possível carregar</b><p>{error}</p></div>
        : items === null ? <div className="aud-skel" aria-hidden="true"><div className="skel" /></div>
        : items.length === 0 ? <div className="empty"><b>Nenhum uso ainda</b><p>Cada transcrição ou voz gerada pela rede aparece aqui, com o nó que processou.</p></div>
        : (
          <ul className="aud-vlist">
            {items.map((u) => {
              const [cls, label] = USE_STATE[u.status] ?? ['', u.status];
              return (
                <li key={u.id} className="aud-v">
                  <span className="aud-v-main">
                    <b>{KIND_LABEL[u.kind]}</b>
                    <span className="aud-v-meta mono">{fmtUnits(u)} · {u.node ?? 'sem nó'} · {when(u.createdAt)}{u.charge > 0 ? ` · ${brl(u.charge)}` : ''}</span>
                  </span>
                  <span className={`pill ${cls}`}>{label}</span>
                </li>
              );
            })}
          </ul>
        )}
    </section>
  );
}

// ---------------------------------------------------------------- Página

export function Audio() {
  const [status, setStatus] = useState<AudioStatus | null>(null);
  const [checking, setChecking] = useState(false);
  const [voices, setVoices] = useState<AudioVoice[] | null>(null);
  const [vError, setVError] = useState<string | null>(null);
  const [usageTick, setUsageTick] = useState(0);   // sobe quando uma transcrição/síntese termina: a lista de usos recarrega
  const used = useCallback(() => setUsageTick((n) => n + 1), []);

  const checkStatus = useCallback(() => {
    setChecking(true);
    api<AudioStatus>('/api/client/audio/status').then(setStatus).catch(() => setStatus(null)).finally(() => setChecking(false));
  }, []);
  const loadVoices = useCallback(() => {
    setVError(null);
    api<AudioVoice[]>('/api/client/audio/voices').then(setVoices).catch((e) => { setVError((e as Error).message); setVoices((cur) => cur); });
  }, []);
  useEffect(() => { checkStatus(); loadVoices(); }, [checkStatus, loadVoices]);

  // enquanto houver voz em treino, pergunta o andamento a cada 5 s (só dessas vozes); ao terminar, avisa
  const pending = (voices ?? []).filter((v) => v.status === 'queued' || v.status === 'processing_audio' || v.status === 'training');
  const pendingKey = pending.map((v) => v.id).join(',');
  useEffect(() => {
    if (!pendingKey) return;
    const ids = pendingKey.split(',');
    let live = true;
    const t = setInterval(async () => {
      const got = (await Promise.all(ids.map((id) => api<{ id: string; status: AudioVoice['status']; error?: string }>(`/api/client/audio/voices/${id}/training-status`).catch(() => null)))).filter((g) => g !== null);
      if (!live || !got.length) return;
      setVoices((cur) => cur && cur.map((v) => { const g = got.find((x) => x.id === v.id); return g ? { ...v, status: g.status, error: g.error } : v; }));
      for (const g of got) if (g.status === 'ready') toast('Voz pronta para usar');
    }, 5000);
    return () => { live = false; clearInterval(t); };
  }, [pendingKey]);

  const mesh = status?.mode === 'mesh';
  // na rede, stt/tts dependem de um NÓ de áudio num pool; o treino de voz ainda usa o serviço direto (Fase B)
  const downNames = status ? [!status.tts && 'texto → áudio', !status.train && 'treino de voz', !status.stt && 'áudio → texto'].filter(Boolean) as string[] : [];
  const nodeDown = status ? [!status.tts && 'texto → áudio', !status.stt && 'áudio → texto'].filter(Boolean) as string[] : [];

  return (
    <div className="page aud-page">
      <header className="ph">
        <div>
          <p className="eyebrow">Síntese de Voz</p>
          <h1>Geração de Áudio</h1>
          <p>Transforme áudio em texto e texto em voz, com a voz Padrão ou com vozes treinadas por você.</p>
        </div>
      </header>

      {downNames.length > 0 && (
        <div className="aud-warn" role="alert">
          <TriangleAlert />
          {mesh
            ? <p>{nodeDown.length > 0 && <><b>Nenhum nó de áudio disponível:</b> {nodeDown.join(', ')}. Conecte uma máquina com os serviços de áudio a uma pool em Workers. </>}{!status?.train && <><b>Treino de voz indisponível:</b> o serviço de treino está fora do ar. </>}Depois, verifique de novo.</p>
            : <p><b>Serviço de voz indisponível:</b> {downNames.join(', ')}. Abra o túnel (<code>scripts/audio-tunnel.sh</code>) e verifique de novo.</p>}
          <button type="button" className="btn sm" onClick={checkStatus} disabled={checking}>{checking ? 'Verificando…' : 'Verificar de novo'}</button>
        </div>
      )}

      <div className="aud-cards">
        <SttCard available={status ? status.stt : null} onUsed={used} />
        <TtsCard voices={voices} available={status ? status.tts : null} onUsed={used} />
      </div>

      <VoicesSection voices={voices} error={vError} status={status} onReload={loadVoices}
        onCreated={(v) => { setVoices((cur) => (cur ? [v, ...cur] : [v])); loadVoices(); }}
        onGone={(id) => setVoices((cur) => (cur ?? []).filter((x) => x.id !== id))} />

      <UsageSection tick={usageTick} />
    </div>
  );
}
